import { useEffect, useMemo, useRef, useState } from 'react';

import {
  buildSmoothPath,
  getDefinedRuns,
  getNiceScale,
} from '../PotentialGraph/potential-chart.utils';

const colors = {
  deltaT: '#79B0B8',
  potential: '#E58C45',
  heatDelivered: '#E58C45',
};

const months = [
  'jan', 'feb', 'mrt', 'apr', 'mei', 'jun',
  'jul', 'aug', 'sep', 'okt', 'nov', 'dec',
];

const margin = {
  top: 16,
  right: 54,
  bottom: 28,
  left: 54,
};

const valueFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat('nl-BE', {
  day: 'numeric',
  month: 'long',
});

function formatPower(value) {
  if (value === undefined || value === null) return '-';

  return `${valueFormatter.format(value)} MW`;
}

function formatTemperature(value) {
  if (value === undefined || value === null) return '-';

  return `${valueFormatter.format(value)} °C`;
}

function formatDay(timestamp) {
  if (!timestamp) return '';

  return dateFormatter.format(new Date(timestamp));
}

function TimelineChart({ points }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const { width, height } = size;
  const plotWidth = Math.max(0, width - margin.left - margin.right);
  const plotHeight = Math.max(0, height - margin.top - margin.bottom);

  const domain = useMemo(() => {
    const year = points.length
      ? new Date(points[0].timestamp).getUTCFullYear()
      : new Date().getUTCFullYear();
    return {
      year,
      start: Date.UTC(year, 0, 1),
      end: Date.UTC(year + 1, 0, 1),
    };
  }, [points]);

  const leftScale = useMemo(() => {
    const maxValue = points.reduce((max, point) => Math.max(
      max,
      point.potential ?? 0,
      point.heatDelivered ?? 0,
    ), 0);

    return getNiceScale(0, maxValue, 4);
  }, [points]);

  const rightScale = useMemo(() => {
    const deltaValues = points
      .map(point => point.deltaT)
      .filter(value => value !== undefined && value !== null && !Number.isNaN(value));
    const minValue = deltaValues.length ? Math.min(...deltaValues) : -1;
    const maxValue = deltaValues.length ? Math.max(...deltaValues) : 0;

    return getNiceScale(minValue, maxValue, 4);
  }, [points]);

  const getX = (time) => {
    const span = domain.end - domain.start || 1;
    return margin.left + ((time - domain.start) / span) * plotWidth;
  };
  const getLeftY = (value) => {
    const span = leftScale.max - leftScale.min || 1;
    return margin.top + plotHeight - ((value - leftScale.min) / span) * plotHeight;
  };
  const getRightY = (value) => {
    const span = rightScale.max - rightScale.min || 1;
    return margin.top + plotHeight - ((value - rightScale.min) / span) * plotHeight;
  };

  const series = useMemo(() => {
    if (plotWidth <= 0 || plotHeight <= 0) return [];

    const buildPaths = (accessor, projectY) => getDefinedRuns(points, accessor)
      .map(run => buildSmoothPath(run.map(point => [
        getX(point.time),
        projectY(accessor(point)),
      ])))
      .filter(Boolean);

    return [
      {
        key: 'potential',
        color: colors.potential,
        dashed: false,
        paths: buildPaths(point => point.potential, getLeftY),
      },
      {
        key: 'heatDelivered',
        color: colors.heatDelivered,
        dashed: true,
        paths: buildPaths(point => point.heatDelivered, getLeftY),
      },
      {
        key: 'deltaT',
        color: colors.deltaT,
        dashed: false,
        paths: buildPaths(point => point.deltaT, getRightY),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, plotWidth, plotHeight, leftScale, rightScale, domain]);

  const monthTicks = useMemo(() => months.map((label, index) => {
    const start = Date.UTC(domain.year, index, 1);
    const next = Date.UTC(domain.year, index + 1, 1);
    return {
      label,
      lineX: getX(start),
      labelX: (getX(start) + getX(next)) / 2,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [domain, plotWidth]);

  const hoveredIndex = hoveredId == null
    ? -1
    : points.findIndex(point => String(point.id) === String(hoveredId));
  const hoveredPoint = hoveredIndex >= 0 ? points[hoveredIndex] : undefined;

  const handleMouseMove = (event) => {
    if (points.length === 0 || plotWidth <= 0) return;

    const bounds = containerRef.current.getBoundingClientRect();
    const relativeX = event.clientX - bounds.left - margin.left;
    const ratio = Math.min(1, Math.max(0, relativeX / plotWidth));
    const targetTime = domain.start + ratio * (domain.end - domain.start);

    let nearest;
    let nearestDistance = Infinity;
    points.forEach((point) => {
      const distance = Math.abs(point.time - targetTime);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = point;
      }
    });
    if (nearest) setHoveredId(nearest.id);
  };

  const hoveredX = hoveredPoint ? getX(hoveredPoint.time) : 0;
  const tooltipOnRight = hoveredX > margin.left + plotWidth / 2;

  return (
    <div className="timeline-chart" ref={containerRef}>
      {width > 0 && height > 0 && (
        <svg
          className="timeline-chart__svg"
          width={width}
          height={height}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredId(null)}
        >
          <text
            className="timeline-chart__axis-unit timeline-chart__axis-unit--left"
            x={22}
            y={margin.top + plotHeight / 2}
            transform={`rotate(-90 22 ${margin.top + plotHeight / 2})`}
          >
            MW
          </text>
          {leftScale.ticks.map(tick => (
            <g key={`left-${tick}`}>
              <line
                className="timeline-chart__gridline"
                x1={margin.left}
                x2={margin.left + plotWidth}
                y1={getLeftY(tick)}
                y2={getLeftY(tick)}
              />
              <text
                className="timeline-chart__axis-label timeline-chart__axis-label--left"
                x={margin.left - 8}
                y={getLeftY(tick)}
              >
                {valueFormatter.format(tick)}
              </text>
            </g>
          ))}
          {rightScale.ticks.map(tick => (
            <text
              key={`right-${tick}`}
              className="timeline-chart__axis-label timeline-chart__axis-label--right"
              x={margin.left + plotWidth + 8}
              y={getRightY(tick)}
            >
              {`${valueFormatter.format(tick)} °C`}
            </text>
          ))}

          {monthTicks.map(tick => (
            <g key={`month-${tick.label}`}>
              <line
                className="timeline-chart__month-tick"
                x1={tick.lineX}
                x2={tick.lineX}
                y1={margin.top + plotHeight}
                y2={margin.top + plotHeight + 4}
              />
              <text
                className="timeline-chart__month-label"
                x={tick.labelX}
                y={margin.top + plotHeight + 16}
              >
                {tick.label}
              </text>
            </g>
          ))}

          {hoveredPoint && (
            <line
              className="timeline-chart__hover-line"
              x1={hoveredX}
              x2={hoveredX}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
          )}

          {series.map(serie => serie.paths.map((path, pathIndex) => (
            <path
              key={`${serie.key}-${pathIndex}`}
              className="timeline-chart__line"
              d={path}
              fill="none"
              stroke={serie.color}
              strokeWidth={2}
              strokeDasharray={serie.dashed ? '6 5' : undefined}
            />
          )))}

          {hoveredPoint && (
            <>
              {hoveredPoint.potential !== undefined && (
                <circle
                  className="timeline-chart__marker"
                  cx={hoveredX}
                  cy={getLeftY(hoveredPoint.potential)}
                  r={3.5}
                  style={{ fill: colors.potential }}
                />
              )}
              {hoveredPoint.heatDelivered !== undefined && (
                <circle
                  className="timeline-chart__marker"
                  cx={hoveredX}
                  cy={getLeftY(hoveredPoint.heatDelivered)}
                  r={3.5}
                  style={{ fill: colors.heatDelivered }}
                />
              )}
              {hoveredPoint.deltaT !== undefined && (
                <circle
                  className="timeline-chart__marker"
                  cx={hoveredX}
                  cy={getRightY(hoveredPoint.deltaT)}
                  r={3.5}
                  style={{ fill: colors.deltaT }}
                />
              )}
            </>
          )}
        </svg>
      )}

      {hoveredPoint && (
        <div
          className={`timeline-chart__tooltip${tooltipOnRight ? ' timeline-chart__tooltip--left' : ''}`}
          style={{ left: `${hoveredX}px`, top: `${margin.top}px` }}
        >
          <div className="timeline-chart__tooltip-title">{formatDay(hoveredPoint.timestamp)}</div>
          <div className="timeline-chart__tooltip-row">
            <span className="timeline-chart__tooltip-swatch" style={{ background: colors.potential }} />
            <span className="timeline-chart__tooltip-label">Bronpotentieel</span>
            <span className="timeline-chart__tooltip-value">{formatPower(hoveredPoint.potential)}</span>
          </div>
          <div className="timeline-chart__tooltip-row">
            <span className="timeline-chart__tooltip-swatch timeline-chart__tooltip-swatch--dashed" style={{ color: colors.heatDelivered }} />
            <span className="timeline-chart__tooltip-label">Vermogen geleverd</span>
            <span className="timeline-chart__tooltip-value">{formatPower(hoveredPoint.heatDelivered)}</span>
          </div>
          <div className="timeline-chart__tooltip-row">
            <span className="timeline-chart__tooltip-swatch" style={{ background: colors.deltaT }} />
            <span className="timeline-chart__tooltip-label">DeltaT</span>
            <span className="timeline-chart__tooltip-value">{formatTemperature(hoveredPoint.deltaT)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export { colors };
export default TimelineChart;
